import { useEffect, useState } from "react";
import { fetchPaidOrder } from "../api/catalog";
import { useCart } from "../cart/CartContext";
import StoreHeader from "../components/StoreHeader";
import StorePixels from "../components/StorePixels";
import { storePath } from "../lib/storePaths";

function formatMoney(amountMinor, currency = "BRL") {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
  }).format(Number(amountMinor || 0) / 100);
}

function formatAddress(address) {
  if (!address) return "";
  const street = [address.line1 || address.street, address.number]
    .filter(Boolean)
    .join(", ");
  return [
    street,
    address.line2 || address.complement,
    address.neighborhood,
    [address.city, address.state].filter(Boolean).join(" - "),
    address.zipCode ? `CEP ${address.zipCode}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

function paymentLabel(payment) {
  if (!payment?.method) return "";
  if (payment.method === "pix") return "PIX";
  const installments = Number(payment.installments || 1);
  const last4 = payment.card?.last4 ? ` • final ${payment.card.last4}` : "";
  if (installments > 1) return `Cartão em ${installments}x${last4}`;
  return `Cartão à vista${last4}`;
}

export default function OrderConfirmedPage({ storeSlug }) {
  const { clearCart } = useCart();
  const orderId = new URLSearchParams(window.location.search).get("orderId");
  const [order, setOrder] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    clearCart();
  }, [clearCart]);

  useEffect(() => {
    if (!orderId) return undefined;
    const controller = new AbortController();
    fetchPaidOrder(storeSlug, orderId, { signal: controller.signal })
      .then(setOrder)
      .catch((error) => {
        if (error.name !== "AbortError") setLoadError(error.message);
      });
    return () => controller.abort();
  }, [orderId, storeSlug]);

  return (
    <div className="storefront-shell">
      <StorePixels
        store={order?.store}
        purchase={
          order
            ? {
                id: order.id,
                value: Number(order.totalMinor || 0) / 100,
                currency: order.currency || "BRL",
                contentIds: (order.items || []).map((item) => item.productId || item.id).filter(Boolean),
              }
            : null
        }
      />
      <StoreHeader
        storeName={order?.store?.name || "Pedido confirmado"}
        storeSlug={storeSlug}
      />
      <main className="order-confirmed">
        <div className="order-confirmed__mark">✓</div>
        <p className="eyebrow">Pagamento aprovado</p>
        <h1>Seu pedido foi confirmado</h1>
        <p>
          Enviamos os detalhes da compra para o seu e-mail.
          {orderId ? ` Pedido ${orderId}.` : ""}
        </p>
        {loadError ? (
          <p className="order-confirmed__hint">{loadError}</p>
        ) : order ? (
          <div className="order-confirmed__summary">
            {(order.items || []).map((item) => (
              <div key={item.id} className="order-confirmed__line">
                <span>
                  {item.title}
                  {item.quantity > 1 ? ` × ${item.quantity}` : ""}
                </span>
                <strong>
                  {formatMoney(item.lineAmountMinor, item.currency)}
                </strong>
              </div>
            ))}
            <div className="order-confirmed__line">
              <span>Frete</span>
              <strong>A cargo da loja</strong>
            </div>
            <div className="order-confirmed__line order-confirmed__total">
              <span>Total</span>
              <strong>{formatMoney(order.totalMinor, order.currency)}</strong>
            </div>
            {paymentLabel(order.payment) ? (
              <p>Pagamento: {paymentLabel(order.payment)}</p>
            ) : null}
            {formatAddress(order.customer?.address) ? (
              <p>Entrega: {formatAddress(order.customer.address)}</p>
            ) : null}
          </div>
        ) : orderId ? (
          <p className="order-confirmed__hint">Carregando pedido...</p>
        ) : null}
        <div className="order-confirmed__actions">
          <a href={storePath("/dashboard", storeSlug)}>Ver meus pedidos</a>
          <a href={storePath("", storeSlug)}>Continuar comprando</a>
        </div>
      </main>
    </div>
  );
}
